import React, { useState, useEffect } from "react";
import { VotingProject } from "../../services/votingService";
import { imageUploadService } from "../../services/firebaseService";

interface VotingProjectFormProps {
  project?: VotingProject | null;
  onClose: () => void;
  onSubmit: (data: { name: string; group: string; poster?: string; youtubeUrl?: string }) => void;
}

const VotingProjectForm: React.FC<VotingProjectFormProps> = ({
  project,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [group, setGroup] = useState("");
  const [poster, setPoster] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setName(project.name || "");
      setGroup(project.group || "");
      setPoster(project.poster || "");
      setYoutubeUrl(project.youtubeUrl || "");
      setImagePreview(project.poster || "");
    }
  }, [project]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setPoster("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !group.trim()) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      setSaving(true);

      let imageUrl = poster;

      // Upload image if a new file is selected
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await imageUploadService.uploadImage(imageFile, "voting");
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
          setUploading(false);
          setSaving(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      onSubmit({
        name: name.trim(),
        group: group.trim(),
        poster: imageUrl?.trim() || undefined,
        youtubeUrl: youtubeUrl.trim() || undefined,
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("Failed to submit form. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-xl shadow-2xl border border-[rgba(59,130,246,0.2)] w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[var(--border-color)]">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-[var(--text-primary)]">
              {project ? "Edit Project" : "Create New Project"}
            </h2>
            <button
              onClick={onClose}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              Project Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--background-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              placeholder="Enter project name"
              required
            />
          </div>

          <div>
            <label
              htmlFor="group"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              Group Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="group"
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--background-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              placeholder="Enter group name"
              required
            />
          </div>

          <div>
            <label
              htmlFor="poster"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              Poster Image
            </label>
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <label
                  htmlFor="image-upload"
                  className="px-4 py-2 bg-[var(--primary-color)] hover:bg-[var(--primary-color-hover)] text-white rounded-lg cursor-pointer transition-colors text-sm font-medium inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-upload"></i>
                  {imageFile ? "Change Image" : "Upload Image"}
                </label>
                <input
                  type="file"
                  id="image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={uploading}
                />
                {imagePreview && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium inline-flex items-center gap-2"
                    disabled={uploading || saving}
                  >
                    <i className="fa-solid fa-trash"></i>
                    Remove
                  </button>
                )}
                {uploading && (
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <span className="text-sm">Uploading...</span>
                  </div>
                )}
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg border border-[var(--border-color)]"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
              {!imagePreview && project?.poster && (
                <div className="mt-3">
                  <p className="text-xs text-[var(--text-secondary)] mb-2">
                    Current image:
                  </p>
                  <img
                    src={project.poster}
                    alt="Current"
                    className="w-full h-48 object-cover rounded-lg border border-[var(--border-color)]"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="youtubeUrl"
              className="block text-sm font-medium text-[var(--text-primary)] mb-2"
            >
              YouTube Video URL
            </label>
            <input
              type="url"
              id="youtubeUrl"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              className="w-full px-4 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--background-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)] focus:border-transparent"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            <p className="text-xs text-[var(--text-secondary)] mt-2">
              Paste a YouTube video URL to display it on the project profile page
            </p>
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-[var(--border-color)]">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] hover:bg-[var(--background-primary)] transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || saving}
              className="px-6 py-2 bg-gradient-to-r from-[var(--gradient-primary)] to-[var(--gradient-secondary)] hover:from-[var(--gradient-secondary)] hover:to-[var(--gradient-primary)] text-white rounded-lg transition-all duration-200 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {saving ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  {uploading ? "Uploading..." : "Saving..."}
                </>
              ) : (
                <>
                  {project ? "Update Project" : "Create Project"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VotingProjectForm;

